// Records that a visitor arrived in the app from a link in a SMASHSEND email, so a flow's click rate can be
// measured against our own purchase data (SMASHSEND's API exposes no flow statistics).
//
// SMASHSEND tags every link in a flow email with `utm_source=smashsend`, `utm_medium=email` and
// `utm_campaign=<flow id>-<step id>`. The arrival URL is read at startup, before the router can rewrite it;
// the event is reported once auth has settled, so a signed-in visitor's click lands on their profile.
//
// Small and dependency-free on purpose: App.tsx imports it at startup (scripts/checkStartupBudget.mjs).

const SMASHSEND_UTM_SOURCE = "smashsend";
// `flw_<id>-<step id>`: both ids are opaque provider identifiers, never customer data.
const SMASHSEND_FLOW_CAMPAIGN_PATTERN = /^(flw_[A-Za-z0-9]{1,64})-([A-Za-z0-9]{1,64})$/;

export const EMAIL_LINK_CLICKED_EVENT = "Email Link Clicked";

export type EmailLinkArrival = {
  email_provider: "smashsend";
  email_flow_id: string | null;
  email_step_id: string | null;
};

/** The SMASHSEND email link this URL came from, or null for any other arrival. */
export function readEmailLinkArrival(search: string): EmailLinkArrival | null {
  const searchParams = new URLSearchParams(search);
  if (searchParams.get("utm_source")?.toLowerCase() !== SMASHSEND_UTM_SOURCE) return null;
  const flowCampaignMatch = SMASHSEND_FLOW_CAMPAIGN_PATTERN.exec(searchParams.get("utm_campaign") ?? "");
  return {
    email_provider: "smashsend",
    // A broadcast or a hand-written campaign name is still a click, without a flow to attribute it to.
    email_flow_id: flowCampaignMatch ? flowCampaignMatch[1] : null,
    email_step_id: flowCampaignMatch ? flowCampaignMatch[2] : null,
  };
}

let pendingEmailLinkArrival: EmailLinkArrival | null = null;

/** At app startup: remembers the email link on the arrival URL until it can be reported. */
export function rememberEmailLinkArrivalAtAppStartup(search: string): void {
  pendingEmailLinkArrival = readEmailLinkArrival(search);
}

/** The remembered arrival, handed over exactly once per page load. */
export function takePendingEmailLinkArrival(): EmailLinkArrival | null {
  const emailLinkArrival = pendingEmailLinkArrival;
  pendingEmailLinkArrival = null;
  return emailLinkArrival;
}
